import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { Color, Modal } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { ModalService, ModalWorkflowService } from '@lsq-frontend/common-ui';
import { uniqueId } from 'lodash';
import { ProgressState } from 'rxjs-uploader';
import { AdditionalUploadsModalComponent } from '../../../customers/modals/additional-uploads-modal.component';
import { UploadsPreviewModalComponent } from '../../../customers/modals/uploads-preview-modal.component';
import { ImagePreviewModalComponent } from '../../../image-preview/image-preview-modal.component';
import { IAdditionalUploadsModalConfig, InvoiceFileUpload } from '../../invoices.models';
import { UploadInvoicesService } from '../upload-invoices.service';

@Component({
    selector: 'lsq-upload-invoices-thumbnail-preview',
    encapsulation: ViewEncapsulation.None,
    template: `
        <div class="upload-invoices-thumbnail-preview">
            <div
                class="thumbnail-preview-container"
                tabindex="0"
                [attr.aria-describedby]="transientId"
                [ngClass]="{ uploaded: isFileUploaded(), failed: isUploadFailed() }"
                (mouseenter)="handleMouseEnter()"
                (mouseleave)="handleMouseLeave()"
            >
                <div
                    class="thumbnail-preview-frame d-flex align-items-start justify-content-start flex-column"
                >
                    <div class="thumbnail-preview-frame-inner">
                        <ng-container *ngIf="!invoiceUpload.hasFailed && !failedLinkedUpload">
                            <lsq-icon
                                *ngIf="!invoiceUpload.previewUrl"
                                class="thumbnail-preview-default-image"
                                [type]="icons.IndividualDocs"
                                [color]="color.GrayDarkest"
                            >
                            </lsq-icon>
                            <img
                                *ngIf="!!invoiceUpload.previewUrl"
                                class="thumbnail-preview-image"
                                [draggable]="false"
                                [src]="invoiceUpload.previewUrl"
                            />
                        </ng-container>
                        <ng-container *ngIf="invoiceUpload.hasFailed">
                            <div class="thumbnail-preview-frame-upload-failed">
                                <p
                                    class="thumbnail-preview-frame-upload-failed-message text-danger"
                                >
                                    Upload Failed
                                </p>
                                <a (click)="invoiceUpload.retry()" href="javascript:void(0)"
                                    >Try Again</a
                                >
                            </div>
                        </ng-container>
                        <ng-container *ngIf="!invoiceUpload.hasFailed && failedLinkedUpload">
                            <div class="thumbnail-preview-frame-upload-failed">
                                <p
                                    class="thumbnail-preview-frame-upload-failed-message text-danger"
                                >
                                    A linked file failed to upload. Please remove it.
                                </p>
                            </div>
                        </ng-container>
                    </div>
                    <div class="thumbnail-preview-title" [id]="transientId">
                        <span>{{ invoiceUpload.name }}</span>
                    </div>
                    <div *ngIf="allowSupportingDocuments" class="thumbnail-preview-after">
                        <div
                            *ngIf="!invoiceUpload?.linkedFileUploads?.length"
                            class="thumbnail-preview-add-supporting-docs"
                        >
                            <span class="thumbnail-preview-add-supporting-docs-label">
                                Add Supporting Documents
                            </span>
                            <lsq-button (buttonClick)="showAdditionalUploadsModal()">
                                Add
                            </lsq-button>
                        </div>
                        <div
                            *ngIf="!!invoiceUpload?.linkedFileUploads?.length"
                            class="thumbnail-preview-added-supporting-docs"
                        >
                            <span class="thumbnail-preview-added-supporting-docs-label">
                                <lsq-icon
                                    [type]="icons.CheckmarkSmall"
                                    [color]="color.Green"
                                ></lsq-icon>
                                {{
                                    invoiceUpload.linkedFileUploads.length
                                        | i18nPlural
                                            : {
                                                  '=1': '# Supporting Document',
                                                  other: '# Supporting Documents'
                                              }
                                }}
                            </span>
                            <div class="thumbnail-preview-added-supporting-docs-actions">
                                <lsq-button
                                    appearance="transparent"
                                    (buttonClick)="showAdditionalUploadsModal(1)"
                                >
                                    View&nbsp;&nbsp;|&nbsp;&nbsp;Add
                                </lsq-button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="thumbnail-preview-ordinal">
                    {{ ordinal }}
                </div>

                <div class="thumbnail-preview-actions" *ngIf="mouseIsOver">
                    <lsq-button
                        class="full-screen-action"
                        *ngIf="!!invoiceUpload.previewUrl && !invoiceUpload.hasFailed"
                        (buttonClick)="showPreviewModal()"
                    >
                        <lsq-icon [type]="icons.FullScreen" [color]="color.White"></lsq-icon>
                    </lsq-button>
                    <lsq-button
                        *ngIf="invoiceUpload.isUploadStatusComplete || invoiceUpload.hasFailed"
                        class="e2e-remove-action"
                        (buttonClick)="remove.emit(invoiceUpload)"
                    >
                        <lsq-icon [type]="icons.Trash" [color]="color.White"></lsq-icon>
                    </lsq-button>
                </div>

                <div
                    class="thumbnail-preview-progress-bar-container"
                    [ngClass]="{ uploaded: isFileUploaded() }"
                >
                    <lsq-progress-bar
                        [value]="getUploadProgressPercentage()"
                        [danger]="isUploadFailed()"
                        [skinny]="true"
                    >
                    </lsq-progress-bar>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./upload-invoices-thumbnail-preview.component.scss']
})
export class UploadInvoicesThumbnailPreviewComponent {
    @Input() public invoiceUpload: InvoiceFileUpload;
    @Input() public allowSupportingDocuments = false;
    @Input() public ordinal: number;
    @Output() public remove = new EventEmitter<InvoiceFileUpload>();

    // State.
    public uploadProgressPercentage = 0;
    public uploadFailed = false;
    public uploadCompleted = false;
    public mouseIsOver = false;
    public transientId = uniqueId('upload-invoices-thumbnail-preview-');

    // Constants.
    public icons = Icons;
    public color = Color;
    public allowedContentTypes: string;

    constructor(
        private _modalService: ModalService,
        private _workflowService: ModalWorkflowService,
        private _uploadInvoicesService: UploadInvoicesService
    ) {
        this.allowedContentTypes = this._uploadInvoicesService.allowedContentTypesForInvoices.join(
            ','
        );
    }

    public get failedLinkedUpload(): boolean {
        return (
            this.invoiceUpload.linkedFileUploads &&
            this.invoiceUpload.linkedFileUploads.some((upload) => upload.hasFailed)
        );
    }

    public showInvoiceUploading(): boolean {
        return (
            this.invoiceUpload &&
            (!this.showSupportingDocumentsUploading() ||
                this.invoiceUpload.progress.state === ProgressState.InProgress)
        );
    }

    public showSupportingDocumentsUploading(): boolean {
        return (
            this.invoiceUpload.linkedFileUploads &&
            this.invoiceUpload.linkedFileUploads.length &&
            this.invoiceUpload.linkedFileUploads.some(
                (upload) => upload.progress.state === ProgressState.InProgress
            )
        );
    }

    public isUploadFailed(): boolean {
        if (this.showInvoiceUploading()) {
            return this.invoiceUpload.hasFailed;
        } else if (this.showSupportingDocumentsUploading()) {
            return this.invoiceUpload.linkedFileUploads.some((upload) => upload.hasFailed);
        }
        return false;
    }

    public isFileUploaded(): boolean {
        if (this.invoiceUpload.isMobileUpload) {
            return true;
        } else if (this.showInvoiceUploading()) {
            return this.invoiceUpload.uploaded;
        } else if (this.showSupportingDocumentsUploading()) {
            return this.invoiceUpload.linkedFileUploads.every((upload) => upload.uploaded);
        }
        return false;
    }

    public getUploadProgressPercentage(): number {
        if (this.showInvoiceUploading()) {
            return this.invoiceUpload.progressPercentage;
        }
        if (this.showSupportingDocumentsUploading()) {
            const uploadProgressPercentageDividend = this.invoiceUpload.linkedFileUploads.reduce(
                (acc, current) => acc + current.progressPercentage,
                0
            );
            const uploadProgressPercentageDivisor =
                this.invoiceUpload.linkedFileUploads.length * 100;
            this.uploadProgressPercentage =
                uploadProgressPercentageDividend / uploadProgressPercentageDivisor;
        }
    }

    public showPreviewModal(): void {
        if (!this.invoiceUpload) {
            return;
        }
        this._modalService.showModal(
            new Modal({
                id: 'image-preview-modal',
                title: this.invoiceUpload.name,
                component: ImagePreviewModalComponent,
                width: 'auto',
                data: {
                    title: this.invoiceUpload.name,
                    imageId: this.invoiceUpload.imageKey,
                    totalPages: this.invoiceUpload.pages,
                    filename: this.invoiceUpload.name
                }
            })
        );
    }

    public showAdditionalUploadsModal(firstStep = 0): void {
        this._workflowService.showWorkflow(
            [
                new Modal({
                    id: 'additional-uploads-modal',
                    title: 'Additional Uploads',
                    component: AdditionalUploadsModalComponent,
                    width: '70rem'
                }),
                new Modal({
                    id: 'uploads-preview-modal',
                    title: 'Additional Uploads',
                    component: UploadsPreviewModalComponent,
                    width: 'auto',
                    className: 'uploads-preview-component',
                    clickOutsideToClose: false
                })
            ],
            firstStep,
            {
                parentFileUpload: this.invoiceUpload,
                mobileUpload: false
            } as IAdditionalUploadsModalConfig
        );
    }

    public handleMouseEnter(): void {
        this.mouseIsOver = true;
    }

    public handleMouseLeave(): void {
        this.mouseIsOver = false;
    }
}
